import Dexie, { type DexieError } from 'dexie';
import { handleBackgroundError } from '@/lib/error-handler';
import { db } from './client';

function isDexieError(value: unknown): value is DexieError {
  return value instanceof Dexie.DexieError;
}

function getInnerDexieError(error: DexieError): DexieError | undefined {
  const innerValue = error.inner as unknown;
  if (
    !innerValue ||
    typeof innerValue !== 'object' ||
    !('name' in innerValue) ||
    typeof (innerValue as { name: unknown }).name !== 'string'
  ) {
    return undefined;
  }
  return innerValue as DexieError;
}

function isUpgradeOrSchemaError(error: DexieError): boolean {
  if (error.name === 'UpgradeError') {
    return true;
  }

  if (error.name === 'DatabaseClosedError') {
    const innerError = getInnerDexieError(error);
    return innerError ? isUpgradeOrSchemaError(innerError) : false;
  }

  return false;
}

async function resetDatabase(): Promise<void> {
  try {
    db.close();
  } catch {
    // Ignore close errors
  }
  await db.delete();
}

export async function initializeDatabase(): Promise<void> {
  try {
    await db.open();
  } catch (error) {
    if (isDexieError(error) && isUpgradeOrSchemaError(error)) {
      handleBackgroundError(error, { operation: 'databaseUpgrade' }, 'warn');
      await resetDatabase();
      await db.open();
      return;
    }

    throw error;
  }
}
